import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { AngularFireDatabase } from '@angular/fire/compat/database';
import { AngularFireFunctions } from '@angular/fire/compat/functions';
import { getOobContinueUrl } from '@client/selectors';
import {
	AuthCodeInfo,
	CodeForm,
	EmailEditForm,
	LoginForm,
	RegisterEmailForm,
	ResetPasswordForm,
	UpdatePasswordForm,
	User
} from '@client/utils/shared-constants';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';

import { DialogCloseResult, DialogRef, DialogService } from '@progress/kendo-angular-dialog';

import { EMPTY, forkJoin, from, Observable, of, throwError, timer, zip } from 'rxjs';
import { catchError, mapTo, switchMap, take, tap } from 'rxjs/operators';
import { environment } from '../../configs/environment';

import { getAuth, PhoneAuthProvider, RecaptchaVerifier, PhoneMultiFactorGenerator } from 'firebase/auth';

@Injectable()
export class AuthService {
	_user: any;
	resolver = null;
	get user() {
		return this._user;
	}

	get authState() {
		return this.afAuth.authState;
	}

	get uid() {
		return this._user.uid ? this._user.uid : null;
	}

	captchaId: number;
	confirmationResult: any;
	recaptchaVerifier: any;
	verificationId = null;

	constructor(
		public afAuth: AngularFireAuth,
		private store: Store<any>,
		private http: HttpClient,
		private dialogService: DialogService,
		private fns: AngularFireFunctions,
		private translate: TranslateService,
		public afDb: AngularFireDatabase
	) {
		this.afAuth.authState.subscribe(x => {
			const u: any = (x && x.toJSON()) || x;
		});
	}

	checkCode(code: string) {
		const refreshUser = this.fns.httpsCallable('refreshUser');
		return forkJoin(
			this.store.select(getOobContinueUrl).pipe(
				take(1),
				switchMap(x => {
					const res = /uid=(.*)$/g.exec(x);

					return res ? refreshUser(res[1]) : of(null);
				})
			),
			this.afAuth.checkActionCode(code)
		).pipe(catchError(e => throwError(this.translate.instant((e && (e.code || e.message)) || 'unknown'))));
	}

	checkPhone(phoneNumber) {
		const url = environment.backendApiUrl + '/spaq';
		return Observable.create(observer => {
			this.http.get(`${url}/check_phone/+1${phoneNumber}`).subscribe(
				_e => {
					observer.next(true);
					observer.complete();
				},
				e => {
					observer.error(this.translate.instant(e.code || 'unknown'));
				}
			);
		});
	}

	clearCaptcha() {
		this.confirmationResult = null;
		if (this.recaptchaVerifier) {
			this.recaptchaVerifier.clear();
		}
	}

	confirmPasswordReset(data: { code: string; newPassword: string }) {
		return from(this.afAuth.confirmPasswordReset(data.code, data.newPassword)).pipe(
			catchError(e => throwError(this.translate.instant(e.code || 'unknown')))
		);
	}

	createUserWithEmailAndPasswordAction(form: RegisterEmailForm): Observable<any> {
		const test = this.fns.httpsCallable('createUser');
		return test({ ...form, host: window.location.hostname + ':' + window.location.port }).pipe(
			catchError(e => {
				const res = e && e.message;
				return throwError(this.translate.instant(res || 'unknown'));
			})
		);
	}

	enableSessionExpire() {
		timer(1000 * 60 * 5).subscribe(_ => {
			this.showConfirmation();
		});
	}

	forgotPassword(model) {
		return Observable.create(observer => {
			try {
				// this.afAuth.languageCode = of('fr').toPromise();
				this.afAuth
					.sendPasswordResetEmail(model.email, {
						url: window.location.protocol + '//' + window.location.hostname + '/auth/login/email'
					})
					.then(_e => {
						observer.next(true);
						observer.complete();
					})
					.catch(e => {
						observer.error(this.translate.instant((e.code || '').replace('/', '.') || 'unknown'));
					});
			} catch (e) {
				observer.error(this.translate.instant((e.code || '').replace('/', '.') || 'unknown'));
			}
		});
	}

	generateCaptcha() {}

	getCode({ companyCode, userCode }: CodeForm) {
		return Observable.create(observer => {
			const url = `https://us-central1-spaq-156518.cloudfunctions.net/getCode?companyCode=${companyCode}&userCode=${userCode}`;
			return this.http.get(url).subscribe(
				data => {
					if (data === null) {
						observer.error({ code: 'not-found', message: 'Code introuvable' });
					} else {
						observer.next(data);
						observer.complete();
					}
				},
				e => {
					observer.error(this.translate.instant(e.code || 'unknown'));
				}
			);
		});
	}

	initCaptcha() {}

	loggedIn() {
		return !!this._user;
	}

	async login({ email, password, code }: LoginForm) {
		try {
			await this.afAuth.setPersistence('session');

			if (code && code !== '') {
				const cred = PhoneAuthProvider.credential(this.verificationId, code);
				const multiFactorAssertion = PhoneMultiFactorGenerator.assertion(cred);

				const r = await this.resolver.resolveSignIn(multiFactorAssertion);

				const x = {
					displayName: r.user.displayName,
					phoneNumber: r.user.phoneNumber,
					email: r.user.email,
					photoURL: r.user.photoURL,
					emailVerified: r.user.emailVerified,
					uid: r.user.uid
				} as User;

				const scope = document.location.hostname.split('.')[0].toUpperCase();

				const domain = document.location.hostname.split('.').splice(1).join('.');

				await this.afDb.object('/logs/' + x.uid + '/login/' + new Date().getTime()).set({
					createdAt: new Date().toISOString(),
					userAgent: navigator.userAgent || '',
					webdriver: navigator.webdriver || false,
					vendor: navigator.vendor || '',
					language: navigator.language || '',
					connection: navigator['connection'] || null,
					scope,
					domain
				});

				return x;
			} else {
				const r = await this.afAuth.signInWithEmailAndPassword(email.trim(), password.trim());
				const x = {
					displayName: r.user.displayName,
					phoneNumber: r.user.phoneNumber,
					email: r.user.email,
					photoURL: r.user.photoURL,
					emailVerified: r.user.emailVerified,
					uid: r.user.uid
				} as User;

				const scope = document.location.hostname.split('.')[0].toUpperCase();

				const domain = document.location.hostname.split('.').splice(1).join('.');

				await this.afDb.object('/logs/' + x.uid + '/login/' + new Date().getTime()).set({
					createdAt: new Date().toISOString(),
					userAgent: navigator.userAgent || '',
					webdriver: navigator.webdriver || false,
					vendor: navigator.vendor || '',
					language: navigator.language || '',
					connection: navigator['connection'] || null,
					scope,
					domain
				});

				return x;
			}
		} catch (e) {
			if (e.code === 'auth/multi-factor-auth-required') {
				const app = await this.afAuth.app;
				const auth = getAuth(app);
				this.resolver = e.resolver;

				if (this.recaptchaVerifier) {
					this.recaptchaVerifier.clear();
				}

				this.recaptchaVerifier = new RecaptchaVerifier('recaptcha-container', { size: 'invisible' }, auth);

				if (this.resolver.hints[0].factorId === PhoneMultiFactorGenerator.FACTOR_ID) {
					const phoneInfoOptions = {
						multiFactorHint: this.resolver.hints[0],
						session: this.resolver.session
					};
					const phoneAuthProvider = new PhoneAuthProvider(auth);
					this.verificationId = await phoneAuthProvider.verifyPhoneNumber(phoneInfoOptions, this.recaptchaVerifier);
				}
			}
			return throwError(this.translate.instant((e.code || '').replace('/', '.') || 'unknown')).toPromise();
		}
	}
	async initCaptchav2() {
		try {
			const app = await this.afAuth.app;
			const auth = getAuth(app);

			this.captchaId = null;
			this.recaptchaVerifier = new RecaptchaVerifier('recaptcha-container', { size: 'visible' }, auth);

			this.captchaId = await this.recaptchaVerifier.render();

			return this.recaptchaVerifier;
		} catch (e) {}
	}
	logout() {
		this.afDb.database.goOffline();
		return from(this.afAuth.signOut()).pipe(
			tap(() => {
				this.afDb.database.goOnline();
			}),
			catchError(e => throwError(this.translate.instant(e.code || 'unknown')))
		);
	}

	onCaptchaExpire() {
		this.clearCaptcha();
	}

	onCaptchaSolve(_result) {}

	register(form: RegisterEmailForm) {
		const test = this.fns.httpsCallable('createUser');
		return test({ ...form, host: window.location.hostname + ':' + window.location.port }).pipe(
			catchError(e => {
				return throwError(e.message);
			})
		);
	}

	reload() {
		return this.afAuth.user.pipe(
			take(1),
			switchMap(u =>
				u
					? from(u.reload()).pipe(
							mapTo(u.toJSON()),
							catchError(e => throwError(this.translate.instant(e.code || 'unknown')))
					  )
					: EMPTY
			)
		);
	}

	renderCaptcha() {}

	resetPassword({ password, code }: ResetPasswordForm) {
		return from(
			this.afAuth
				.confirmPasswordReset(code, password)
				.then(_e => {
					return { code, password };
				})
				.catch(e => throwError(this.translate.instant(e.code || 'unknown')))
		);
	}

	sendEmailVerificationLink() {
		// this.afAuth.languageCode = of('fr').toPromise();

		return this.afAuth.user.pipe(
			take(1),
			switchMap(u =>
				from(
					u.sendEmailVerification({
						url: window.location.protocol + '//' + window.location.hostname + '/?uid=' + u.uid
					})
				).pipe(catchError(e => throwError(this.translate.instant(e.code || 'unknown'))))
			)
		);
	}

	public showConfirmation() {
		const dialog: DialogRef = this.dialogService.open({
			title: 'Session expirée',
			content: 'Votre session est expirée. Souhaitez-vous rester en ligne?',
			actions: [{ text: 'Non' }, { text: 'Oui', primary: true }],
			width: 450,
			height: 200,
			minWidth: 250
		});
		const signOut = timer(1000 * 60).subscribe(_ => {
			dialog.close();
		});
		dialog.result.subscribe(result => {
			if (result instanceof DialogCloseResult) {
				return this.logout();
			} else {
				if (result.text === 'Oui') {
					signOut.unsubscribe();
					this.enableSessionExpire();
				} else {
					return this.logout();
				}
			}
		});
	}

	solveCaptcha(code: string) {
		return from(
			this.confirmationResult.confirm(code).then(result => {
				const user = {
					displayName: result.user.displayName,
					phoneNumber: result.user.phoneNumber,
					email: result.user.email,
					photoURL: result.user.photoURL,
					emailVerified: result.user.emailVerified,
					uid: result.user.uid
				} as User;
				this.clearCaptcha();
				return user;
			})
		).pipe(catchError(e => throwError(this.translate.instant(e.code || 'unknown'))));
	}

	updateEmail(form: EmailEditForm) {
		return this.afAuth.user.pipe(
			take(1),
			switchMap(u =>
				this.login({ email: u.email, password: form.current_password, code: form.code }).then(() => {
					return u.updateEmail(form.email).then(() =>
						u.sendEmailVerification({
							url:
								window.location.protocol +
								'//' +
								window.location.hostname +
								'/updateEmail?uid=' +
								u.uid +
								'&d=' +
								new Date().getTime()
						})
					);
				})
			)
		);
	}

	updatePassword(data: UpdatePasswordForm): Observable<any> {
		return this.afAuth.user.pipe(
			take(1),
			switchMap(u =>
				this.login({ email: u.email, password: data.current_password, code: data.code }).then(() => {
					return u.updatePassword(data.password).catch(e => throwError(this.translate.instant(e.code || 'unknown')));
				})
			)
		);
	}

	validateCode(code) {
		return Observable.create(observer => {
			this.afAuth
				.checkActionCode(code)
				.then(_e => {
					observer.next(true);
					observer.complete();
				})
				.catch(e => throwError(this.translate.instant(e.code || 'unknown')));
		});
	}

	validateEmailCode(data: AuthCodeInfo) {
		const refreshUser = this.fns.httpsCallable('refreshUser');
		return zip(
			this.store.select(getOobContinueUrl).pipe(
				switchMap(x => {
					const res = /uid=(.*)$/g.exec(x);

					if (res) {
						return res ? refreshUser(res[1]) : of(null);
					}
				})
			),
			this.afAuth.applyActionCode(data.oobCode)
		).pipe(catchError(e => throwError(this.translate.instant((e && (e.code || e.message)) || 'unknown'))));
	}
}
